import {AGENCY_KEYS} from "@/api/agencyApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import CircleWithTickIcon from "@/assets/colorable/circle_with_tick.svg?react";
import CopyIcon from "@/assets/colorable/copy.svg?react";
import EditBoxIcon from "@/assets/colorable/edit_box.svg?react";
import ShieldIcon from "@/assets/colorable/shield.svg?react";
import Button from "@/components/Common/Button";
import CopyToClipboardButton from "@/components/Common/CopyToClipboardButton";
import {useFetchUser} from "@/hooks/useFetchUser";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import type {TReferralTab} from "@/store/types";
import type {TError} from "@/utils/errorHandlers";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {useStateWithDeps} from "use-state-with-deps";

const ReferralCodeForm: FC<{
  agencyId?: number | null;
  companyId?: number | null;
  onCopy?: () => void;
  readOnly?: boolean;
  referralCode: string;
  type?: TReferralTab;
}> = ({agencyId, companyId, onCopy, readOnly, referralCode, type = "private"}) => {
  const {t} = useTranslation(["common", "referral", "errors"]);
  const queryClient = useQueryClient();
  const {refetch: refetchUser} = useFetchUser();
  const isPublic = type === "public";
  const mutationKey = !!agencyId ? AGENCY_KEYS.mAgency(agencyId!) : COMPANY_KEYS.mCompany(companyId!);
  const [isEditingReferralCode, setIsEditingReferralCode] = useState(false);
  const [newReferralCode, setNewReferralCode] = useStateWithDeps(referralCode, [referralCode]);
  const [error, setError] = useState("");

  const {mutate: handleAccountUpdate, isPending: isUpdatingAccount} = useMutation({
    meta: {skipErrorToast: true, skipSuccessToast: true},
    mutationKey,
    mutationFn: (referralCode: string) =>
      client.patch(mutationKey[0], isPublic ? {referral_code_public: referralCode} : {referral_code: referralCode}),
    onSuccess: () => {
      refetchUser();
      if (!!agencyId) {
        queryClient.refetchQueries({queryKey: AGENCY_KEYS.qAgency(agencyId)});
      }

      if (!!companyId) {
        queryClient.refetchQueries({queryKey: COMPANY_KEYS.qCompany(companyId)});
      }
      setIsEditingReferralCode(false);
      setError("");
    },
    onError: (error: TError) => {
      const detail = error.response?.data?.detail;
      if (detail === "private_and_public_referral_code_cannot_be_same") {
        setError(t("errors:private_and_public_referral_code_cannot_be_same"));
      } else if (detail === "referral_code_already_exists" || detail === "public_referral_code_already_exists") {
        setError(t("referralCodeAlreadyExists"));
      } else {
        setError((detail as string) ?? "");
      }
    },
  });

  const onCancel = () => {
    setNewReferralCode(referralCode);
    setIsEditingReferralCode(false);
    setError("");
  };

  const pill = isPublic
    ? {
        Icon: ShieldIcon,
        bgClass: "bg-primary-powder",
        iconClass: "text-primary-dark-solid h-2.5 w-2",
        labelKey: "referral:vetted_through_refluenced" as const,
      }
    : {
        Icon: CircleWithTickIcon,
        bgClass: "bg-success-light-green",
        iconClass: "text-success-herbal size-2.5",
        labelKey: "referral:trusted_by_you" as const,
      };

  const actionButtons = isEditingReferralCode ? (
    <>
      <Button
        buttonStyle="White"
        className="h-8 gap-2 px-6 text-sm sm:h-12 sm:text-base"
        isDisabled={isUpdatingAccount}
        onClick={onCancel}>
        {t("cancel")}
      </Button>
      <Button
        buttonStyle="Blue"
        className="h-8 gap-2 px-6 text-sm sm:h-12 sm:text-base"
        isDisabled={isUpdatingAccount}
        onClick={() => handleAccountUpdate(newReferralCode)}>
        {t("Save")}
      </Button>
    </>
  ) : (
    <>
      <Can I="update" a="CompanyReferral">
        <Button
          buttonStyle="White"
          className="h-8 gap-2 px-6 text-sm sm:h-12 sm:text-base"
          onClick={() => setIsEditingReferralCode(true)}>
          <EditBoxIcon className="size-3.5 shrink-0" />
          {t("Edit")}
        </Button>
      </Can>
      <CopyToClipboardButton
        buttonStyle="Blue"
        className="h-8 gap-2 px-6 text-sm sm:h-12 sm:text-base"
        contentToCopy={referralCode}
        icon={<CopyIcon className="size-3.5 shrink-0" />}
        label={t("referral:copy")}
      />
    </>
  );

  if (readOnly) {
    return (
      <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex h-15 w-full items-center justify-between gap-4 rounded-md border px-3">
        <div className="flex flex-col gap-3">
          <span className="text-neutral-grey-light text-2xs leading-2 font-semibold uppercase">
            {t("referral:referral_code")}
          </span>
          <span className="text-xl leading-3.5 font-semibold">{newReferralCode}</span>
        </div>
        <CopyToClipboardButton
          buttonStyle="Custom"
          className="h-12 w-fit gap-2.5 bg-white px-6 text-base enabled:hover:scale-[1.01]"
          contentToCopy={referralCode}
          icon={<CopyIcon className="size-3.5 shrink-0" />}
          label={t("referral:copy")}
          onCopyCallback={onCopy}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col justify-between sm:flex-row sm:items-center">
        <span className="text-neutral-black text-sm font-semibold">
          {t(isPublic ? "referral:public_referral_code" : "referral:private_referral_code")}
        </span>
        <div className={clsxm("flex min-h-5 w-fit items-center gap-1 rounded-full px-3 py-1", pill.bgClass)}>
          <pill.Icon className={clsxm("shrink-0", pill.iconClass)} />
          <span className="text-neutral-black text-2xs leading-4 font-semibold tracking-wide uppercase">
            {t(pill.labelKey)}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div
          className={clsxm(
            "border-neutral-grey-light-dark flex h-15 items-center justify-between gap-4 rounded-md border bg-white px-3",
            !isEditingReferralCode && "bg-neutral-grey-lightest",
          )}>
          <input
            className="flex h-12 w-full items-center justify-center text-xl font-semibold outline-hidden"
            disabled={!isEditingReferralCode}
            onChange={e => setNewReferralCode(e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))}
            value={newReferralCode}
          />
          <div className="hidden items-center gap-3 sm:flex">{actionButtons}</div>
        </div>
        {!!error && <span className="text-danger-medium-red mt-0.5 text-xs">{error}</span>}
        <div className="flex items-center gap-3 self-end sm:hidden">{actionButtons}</div>
      </div>
    </div>
  );
};

export default ReferralCodeForm;
